import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiChevronDown, FiCreditCard, FiFileText, FiHelpCircle } from 'react-icons/fi';
import '../styles/subscription-menu.css';

export default function SubscriptionMenuButton({ onSelect }) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState(null);

  const items = useMemo(
    () => [
      { id: 'billing', label: 'Billing', icon: FiCreditCard },
      { id: 'invoice', label: 'Invoice', icon: FiFileText },
      { id: 'help', label: 'Help', icon: FiHelpCircle }
    ],
    []
  );

  useEffect(() => {
    if (!open) return undefined;

    const onMouseDown = (e) => {
      const btn = buttonRef.current;
      const menu = menuRef.current;
      const t = e.target;
      if (!btn || !menu || !(t instanceof Node)) return;
      if (btn.contains(t) || menu.contains(t)) return;
      setOpen(false);
    };

    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;

    const compute = () => {
      const btn = buttonRef.current;
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      const top = r.bottom + 10; // match CSS offset
      const right = Math.max(12, window.innerWidth - r.right);
      setMenuStyle({
        position: 'fixed',
        top: `${Math.round(top)}px`,
        right: `${Math.round(right)}px`
      });
    };

    compute();
    window.addEventListener('resize', compute);
    // capture scrolls from nested containers too
    window.addEventListener('scroll', compute, true);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [open]);

  const handleItemClick = (id) => {
    setOpen(false);
    onSelect?.(id);
  };

  return (
    <div className="subscription-menu" data-open={open ? 'true' : 'false'}>
      <button
        ref={buttonRef}
        type="button"
        className="subscription-menu-trigger"
        aria-label="Subscription"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <FiCreditCard className="subscription-menu-trigger-icon" aria-hidden="true" />
        <span>Subscription</span>
        <FiChevronDown className="subscription-menu-caret" aria-hidden="true" />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            className="subscription-menu-panel"
            role="menu"
            style={menuStyle || undefined}
          >
            {items.map((it) => {
              const Icon = it.icon;
              return (
                <button
                  key={it.id}
                  type="button"
                  className="subscription-menu-item"
                  role="menuitem"
                  onClick={() => handleItemClick(it.id)}
                >
                  <Icon className="subscription-menu-item-icon" aria-hidden="true" />
                  <span>{it.label}</span>
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </div>
  );
}

